import { useState, useEffect } from "react";
import { Table, Input, Tag, Button, Tooltip, Spin, Space, message } from "antd";
import {
  DownloadOutlined,
  EyeOutlined,
  CheckOutlined,
  LockOutlined,
  UnlockOutlined,
} from "@ant-design/icons";
import http from "../../helpers/http";
import config from "../../config/config";
import helper from "../../helpers/helper";

const BusinessInvoices = () => {
  const [dataSource, setDataSource] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [billingNameDrafts, setBillingNameDrafts] = useState({});
  const [savingBillingName, setSavingBillingName] = useState({});
  const [invoiceNoDrafts, setInvoiceNoDrafts] = useState({});
  const [savingInvoiceNo, setSavingInvoiceNo] = useState({});

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const { data } = await http.get(
        `${config.api_url}admin/business-users/invoices`
      );
      if (data) {
        // Flatten: one row per payment, with business/subscription info
        const rows = [];
        (data || []).forEach((sub) => {
          if (sub.payments && sub.payments.length > 0) {
            sub.payments.forEach((payment) => {
              rows.push({
                key: payment._id,
                businessName: sub.business?.business_name || "Unknown",
                email: sub.business?.email || "",
                planName: sub.plan?.label || sub.plan?.name || "Supporter",
                amount: payment.amount,
                startDate: sub.startDate,
                endDate: sub.endDate,
                invoiceNo: payment.invoiceNo || "-",
                billingName: payment.billingDetails?.billingName || "",
                paymentId: payment._id,
                paymentDate: payment.paymentDatetime,
                paymentMethod: payment.paymentMethod,
                invoiceLockAt: payment.invoiceLockAt,
                isInvoiceLocked: !!payment.isInvoiceLocked,
              });
            });
          } else {
            // Subscription with no payments yet
            rows.push({
              key: sub._id,
              businessName: sub.business?.business_name || "Unknown",
              email: sub.business?.email || "",
              planName: sub.plan?.label || sub.plan?.name || "Supporter",
              amount: sub.plan?.amount || 0,
              startDate: sub.startDate,
              endDate: sub.endDate,
              invoiceNo: "-",
              paymentId: null,
              paymentDate: null,
              paymentMethod: null,
            });
          }
        });
        setDataSource(rows);
        setFilteredData(rows);
        const seededBillingDrafts = {};
        const seededInvoiceDrafts = {};
        rows.forEach((r) => {
          if (r.paymentId) {
            seededBillingDrafts[r.paymentId] = r.billingName || "";
            seededInvoiceDrafts[r.paymentId] = r.invoiceNo || "";
          }
        });
        setBillingNameDrafts(seededBillingDrafts);
        setInvoiceNoDrafts(seededInvoiceDrafts);
      }
    } catch (error) {
      console.error("Failed to fetch invoices:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoices();
  }, []);

  const handleSearch = (e) => {
    const value = e.target.value.toLowerCase();
    setSearchTerm(value);
    if (!value) {
      setFilteredData(dataSource);
      return;
    }
    setFilteredData(
      dataSource.filter((item) =>
        [item.businessName, item.email, item.invoiceNo]
          .join(" ")
          .toLowerCase()
          .includes(value)
      )
    );
  };

  const handleDownload = (paymentId) => {
    const url = `${config.api_url}business-plans/download-invoice/${paymentId}`;
    const link = document.createElement("a");
    link.href = url;
    link.target = "_blank";
    link.click();
  };

  const handleView = (paymentId) => {
    const url = `${config.api_url}business-plans/download-invoice/${paymentId}?inline=1`;
    window.open(url, "_blank", "noopener");
  };

  const handleSaveInvoiceNo = async (paymentId) => {
    const newNo = invoiceNoDrafts[paymentId] ?? "";
    setSavingInvoiceNo((s) => ({ ...s, [paymentId]: true }));
    try {
      const res = await http.put(
        `${config.api_url}admin/business-users/invoices/${paymentId}/invoice-no`,
        { invoiceNo: newNo }
      );
      if (res?.data) {
        const updater = (prev) =>
          prev.map((r) =>
            r.paymentId === paymentId ? { ...r, invoiceNo: newNo } : r
          );
        setDataSource(updater);
        setFilteredData(updater);
        message.success("Invoice number updated");
      } else {
        message.error("Failed to update invoice number");
      }
    } catch (e) {
      message.error("Failed to update invoice number");
    } finally {
      setSavingInvoiceNo((s) => ({ ...s, [paymentId]: false }));
    }
  };

  const handleSaveBillingName = async (paymentId) => {
    const newName = billingNameDrafts[paymentId] ?? "";
    setSavingBillingName((s) => ({ ...s, [paymentId]: true }));
    try {
      const res = await http.put(
        `${config.api_url}admin/business-users/invoices/${paymentId}/billing-name`,
        { billingName: newName }
      );
      if (res?.data) {
        const applied = res.data?.data?.appliedToInvoice !== false;
        if (applied) {
          const updater = (prev) =>
            prev.map((r) =>
              r.paymentId === paymentId ? { ...r, billingName: newName } : r
            );
          setDataSource(updater);
          setFilteredData(updater);
          message.success("Billing name updated");
        } else {
          // Invoice is frozen: the server only updated the business account.
          // Reset the draft so the row keeps showing the name on the invoice.
          const saved =
            dataSource.find((r) => r.paymentId === paymentId)?.billingName || "";
          setBillingNameDrafts((d) => ({ ...d, [paymentId]: saved }));
          message.warning(
            "Invoice is locked — billing name saved for future invoices only"
          );
        }
      } else {
        message.error("Failed to update billing name");
      }
    } catch (e) {
      message.error("Failed to update billing name");
    } finally {
      setSavingBillingName((s) => ({ ...s, [paymentId]: false }));
    }
  };

  const columns = [
    {
      title: "Business Name",
      dataIndex: "businessName",
      key: "businessName",
      sorter: (a, b) => a.businessName?.localeCompare(b.businessName),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
    },
    {
      title: "Plan",
      dataIndex: "planName",
      key: "planName",
      render: (text) => <Tag color="green">{text}</Tag>,
    },
    {
      title: "Amount Paid",
      dataIndex: "amount",
      key: "amount",
      render: (amount) => (amount ? `₹${amount}` : "-"),
      sorter: (a, b) => (a.amount || 0) - (b.amount || 0),
    },
    {
      title: "Plan Start",
      dataIndex: "startDate",
      key: "startDate",
      render: (date) => (date ? helper.ISTDate(date) : "-"),
      sorter: (a, b) =>
        new Date(a.startDate || 0) - new Date(b.startDate || 0),
    },
    {
      title: "Plan End",
      dataIndex: "endDate",
      key: "endDate",
      render: (date) => (date ? helper.ISTDate(date) : "-"),
    },
    {
      title: "Invoice No",
      dataIndex: "invoiceNo",
      key: "invoiceNo",
      render: (_, record) => {
        if (!record.paymentId) return "-";
        const draft = invoiceNoDrafts[record.paymentId] ?? "";
        const saved = record.invoiceNo || "";
        const dirty = draft !== saved;
        const saving = !!savingInvoiceNo[record.paymentId];
        return (
          <Space.Compact style={{ width: 200 }}>
            <Input
              value={draft}
              onChange={(e) =>
                setInvoiceNoDrafts((d) => ({
                  ...d,
                  [record.paymentId]: e.target.value,
                }))
              }
              disabled={saving}
              placeholder="Invoice No"
              onPressEnter={() => {
                if (dirty && !saving) handleSaveInvoiceNo(record.paymentId);
              }}
            />
            <Tooltip title="Save invoice number">
              <Button
                type="primary"
                icon={<CheckOutlined />}
                disabled={!dirty || saving}
                loading={saving}
                onClick={() => handleSaveInvoiceNo(record.paymentId)}
              />
            </Tooltip>
          </Space.Compact>
        );
      },
    },
    {
      title: "Billing Lock",
      dataIndex: "isInvoiceLocked",
      key: "isInvoiceLocked",
      filters: [
        { text: "Editable", value: false },
        { text: "Locked", value: true },
      ],
      onFilter: (value, record) =>
        !!record.paymentId && record.isInvoiceLocked === value,
      render: (_, record) => {
        if (!record.paymentId) return "-";
        const lockAt = record.invoiceLockAt
          ? helper.ISTDate(record.invoiceLockAt, "DD MMM YYYY")
          : "-";
        return record.isInvoiceLocked ? (
          <Tooltip title={`Frozen since ${lockAt} (00:00 IST)`}>
            <Tag icon={<LockOutlined />} color="default">
              Locked
            </Tag>
          </Tooltip>
        ) : (
          <Tooltip
            title={`Billing changes apply to this invoice until ${lockAt} 00:00 IST`}
          >
            <Tag icon={<UnlockOutlined />} color="green">
              Editable till {lockAt}
            </Tag>
          </Tooltip>
        );
      },
    },
    {
      title: "Billing Name",
      dataIndex: "billingName",
      key: "billingName",
      render: (_, record) => {
        if (!record.paymentId) return "-";
        const draft = billingNameDrafts[record.paymentId] ?? "";
        const saved = record.billingName || "";
        const dirty = draft !== saved;
        const saving = !!savingBillingName[record.paymentId];
        const locked = !!record.isInvoiceLocked;
        return (
          <Tooltip
            title={
              locked
                ? "Invoice is locked. Edit the billing name on the business account instead; it applies to their next payment."
                : ""
            }
          >
            <Space.Compact style={{ width: 240 }}>
              <Input
                value={draft}
                onChange={(e) =>
                  setBillingNameDrafts((d) => ({
                    ...d,
                    [record.paymentId]: e.target.value,
                  }))
                }
                disabled={saving || locked}
                placeholder="Enter billing name"
                onPressEnter={() => {
                  if (dirty && !saving) handleSaveBillingName(record.paymentId);
                }}
              />
              <Tooltip title={locked ? "" : "Save billing name"}>
                <Button
                  type="primary"
                  icon={<CheckOutlined />}
                  disabled={!dirty || saving || locked}
                  loading={saving}
                  onClick={() => handleSaveBillingName(record.paymentId)}
                />
              </Tooltip>
            </Space.Compact>
          </Tooltip>
        );
      },
    },
    {
      title: "View / Download",
      key: "actions",
      render: (_, record) =>
        record.paymentId ? (
          <Space>
            <Tooltip title="View Invoice">
              <Button
                shape="circle"
                icon={<EyeOutlined />}
                onClick={() => handleView(record.paymentId)}
              />
            </Tooltip>
            <Tooltip title="Download Invoice">
              <Button
                type="primary"
                shape="circle"
                icon={<DownloadOutlined />}
                onClick={() => handleDownload(record.paymentId)}
              />
            </Tooltip>
          </Space>
        ) : (
          "-"
        ),
    },
  ];

  return (
    <div>
      <h2 style={{ marginBottom: 16 }}>Invoices - Supporter Plan</h2>
      <Input.Search
        placeholder="Search by business name, email or invoice no..."
        value={searchTerm}
        onChange={handleSearch}
        style={{ marginBottom: 16, maxWidth: 400 }}
        allowClear
      />
      <Spin spinning={loading}>
        <Table
          rowKey="key"
          columns={columns}
          dataSource={filteredData}
          pagination={{
            defaultPageSize: 10,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100"],
          }}
        />
      </Spin>
    </div>
  );
};

export default BusinessInvoices;
